import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import Button, { ButtonColorEnum, ButtonSizeEnum, ButtonVariantEnum } from 'components/stateless/Button/Button';
import { FormBorder } from 'components/stateless/CommonForm/FormBorder';
import { FormContents } from 'components/stateless/CommonForm/FormContents';
import FormInput from 'components/stateless/CommonForm/FormInput';
import { BackGroundType, FormSubtitle } from 'components/stateless/CommonForm/FormSubtitle';
import FormRadio from 'components/stateless/CommonForm/form-radio/FormRadio';
import { FormRadioWrap } from 'components/stateless/CommonForm/form-radio/FormRadioWrap';
import GuideMessage from 'components/stateless/GuideMessage/GuideMessage';
import Pagination from 'components/stateless/Pagination/Pagination';
import TableBody from 'components/stateless/Table/TableBody';
import TableBorder from 'components/stateless/Table/TableBorder';
import type { HeaderType } from 'components/stateless/Table/TableHeader';
import TableHeader from 'components/stateless/Table/TableHeader';
import Td from 'components/stateless/Table/Td';
import Tr from 'components/stateless/Table/Tr';
import { BackHeaderTitle } from 'components/stateless/Title/BackHeaderTitle';
import { SectionTitle } from 'components/stateless/Title/SectionTitle';
import { ROUTES_SY } from 'constants/routes/system';
import { DuplicateExceptionCode } from 'enums/exception';
import useMounted from 'hooks/useMounted';
import usePageable from 'hooks/usePageable';
import type Pageable from 'models/Pageable';
import type { AnchorPartnerAccountVOModel } from 'models/vo/AnchorPartnerAccountVO';
import type { BankCodeVOModel } from 'models/vo/BankCodeVO';
import type { WaitingBankCodeVOModel } from 'models/vo/WaitingBankCodeVO';
import { formErrorHandler } from 'utils/error/manager';
import { requestCmBankCodeDetail } from 'utils/http/api/common/bank-code';
import { requestSyAnchorPartnerAccountsList } from 'utils/http/api/system/anchor-partner-accounts';
import {
  requestSyWaitingBankCodeList,
  requestSyWaitingBankCodeRegister,
} from 'utils/http/api/system/waiting-bank-code';
import type { SyWaitingBankCodeRegisterRequest } from 'utils/http/api/system/waiting-bank-code/request';
import { ModalType } from 'utils/modal/ModalWrapper';
import useModal from 'utils/modal/useModal';
import { requestDTOParser } from 'utils/valueManager/ValueManager';

function SystemBankCodeDetailConfirmed() {
  const { t } = useTranslation(['format']);
  const mounted = useMounted();
  const modal = useModal();
  const [bankCodeDetail, setBankCodeDetail] = useState<BankCodeVOModel>();
  const [waitingBankCodeList, setWaitingBankCodeList] = useState<Pageable<WaitingBankCodeVOModel[]>>();
  const [bankAccountList, setBankAccountList] = useState<Pageable<AnchorPartnerAccountVOModel[]>>();
  const history = useHistory();

  const bankAccountPageable = usePageable();
  const editHistoryPageable = usePageable();

  const [editable, setEditable] = useState<boolean>(false);

  const { register, getValues, reset, errors, setError, clearErrors } = useForm<SyWaitingBankCodeRegisterRequest>({
    mode: 'onSubmit',
    shouldFocusError: true,
  });

  const { bankCodeId } = useParams<any>();

  useEffect(() => {
    if (mounted) {
      fetchAll();
    }
  }, [mounted]);

  const fetchAll = async () => {
    try {
      const [fetchBankCodeDetailResponse, fetchBankCodeHistory, fetchAnchorPartnerAccounts] = await Promise.all([
        requestCmBankCodeDetail(bankCodeId),
        requestSyWaitingBankCodeList(
          editHistoryPageable.pageable.currentPage,
          editHistoryPageable.pageable.sizePerPage,
          { targetBankCodeId: bankCodeId },
        ),
        requestSyAnchorPartnerAccountsList(
          bankAccountPageable.pageable.currentPage,
          bankAccountPageable.pageable.sizePerPage,
          {
            accountBankCodeId: bankCodeId,
          },
        ),
      ]);
      ReactDOM.unstable_batchedUpdates(() => {
        setBankCodeDetail(fetchBankCodeDetailResponse);
        setWaitingBankCodeList(fetchBankCodeHistory);
        editHistoryPageable.setPageable(fetchBankCodeHistory);
        setBankAccountList(fetchAnchorPartnerAccounts);
        bankAccountPageable.setPageable(fetchAnchorPartnerAccounts);
        resetForm(fetchBankCodeDetailResponse);
        setEditable(false);
      });
    } catch (e) {
      modal.show(e);
    }
  };

  const resetForm = (data: BankCodeVOModel) => {
    reset({
      bankCode: data.bankCode,
      branchCode: data.branchCode,
      bankName: data.bankName,
      branchName: data.branchName,
      deleted: String(data.deleted),
      financierId: data.financierId,
    });
  };
  const renderButtons = () => {
    const onClickCancel = (e: any) => {
      e.preventDefault();
      modal.show(
        <h6>
          {t('text:Would_you_like_to_cancel_the_revision?')}
          <br />
          {t('text:If_you_cancel,_the_entered_contents_will_not_be_saved_and_you_will_have_to_proceed_again')}
        </h6>,
        {
          modalType: ModalType.CONFIRM,
          closeBtnText: t('text:Close'),
          confirmBtnText: t('text:Confirm'),
          confirmBtnCb: () => {
            if (bankCodeDetail) {
              resetForm(bankCodeDetail);
              setEditable(prevState => !prevState);
            }
          },
        },
      );
    };

    const onClickEdit = () => {
      setEditable(prevState => !prevState);
    };

    const onClickSave = (e: any) => {
      e.preventDefault();
      const requestSave = async () => {
        const showSubmitSuccessModal = () => {
          modal.show(
            <h6>
              {t('text:The_request_has_been_submitted_successfully')}
              <br />
              {t('text:See_the_request_in_the_Waiting_for_Confirmation_tab')}
            </h6>,
            {
              modalType: ModalType.ALERT,
              closeBtnText: t('text:OK'),
              closeBtnCb: () => history.push(ROUTES_SY.MANAGEMENT.BANK_CODE_LIST),
            },
          );
        };

        try {
          const data = getValues();
          data.targetBankCodeId = bankCodeId;
          requestDTOParser(data);
          await requestSyWaitingBankCodeRegister(data);
          showSubmitSuccessModal();
        } catch (e: any) {
          if (e.message === DuplicateExceptionCode.BANK_CODE_DUPLICATE) {
            modal.show(<h6>{t('text:The_bank_or_branch_codes_have_already_been_registered')}</h6>);
          } else {
            modal.show(e);
          }
          formErrorHandler<SyWaitingBankCodeRegisterRequest>(e, setError, clearErrors);
        }
      };
      modal.show(<h6>{t('text:Would_you_like_to_request_the_revision_of_the_bank_code_information?')}</h6>, {
        modalType: ModalType.CONFIRM,
        closeBtnText: t('text:Cancel'),
        confirmBtnCb: () => requestSave(),
      });
    };

    return editable ? (
      <>
        <Button
          size={ButtonSizeEnum.SM}
          variant={ButtonVariantEnum.OUTLINED}
          color={ButtonColorEnum.SECONDARY}
          onClick={onClickCancel}
        >
          {t('text:Cancel')}
        </Button>
        <Button size={ButtonSizeEnum.SM} onClick={onClickSave} style={{ width: '60px' }}>
          {t('text:Save')}
        </Button>
      </>
    ) : (
      <Button size={ButtonSizeEnum.SM} onClick={onClickEdit} style={{ width: '60px' }}>
        {t('text:Edit')}
      </Button>
    );
  };

  const bankAccountInfoTable = () => {
    const bankAccountInfoTableHeaders: HeaderType[] = [
      {
        headerText: t('text:Associated_Master_Agreement_of_Anchor_Number'),
      },
      {
        headerText: t('text:Associated_Anchor_Name'),
      },
      {
        headerText: t('text:Responsible_Bank_Name'),
      },
      {
        headerText: t('text:Responsible_Branch_Name'),
      },
      {
        headerText: t('text:Uploaded_Partner_Name'),
      },
      {
        headerText: t('text:Partner_Tax_Code'),
      },
      {
        headerText: t('text:Bank_Account_Holder'),
      },
      {
        headerText: t('text:Bank_Account_Number'),
      },
    ];

    const bankAccountPaginate = async (pageNumber: number, sizePerPage: number) => {
      try {
        const fetchAnchorPartnerAccounts = await requestSyAnchorPartnerAccountsList(pageNumber, sizePerPage, {
          accountBankCodeId: bankCodeId,
        });
        ReactDOM.unstable_batchedUpdates(() => {
          setBankAccountList(fetchAnchorPartnerAccounts);
          bankAccountPageable.setPageable(fetchAnchorPartnerAccounts);
        });
      } catch (e) {
        modal.show(e);
      }
    };

    return (
      <div className="content-area">
        <SectionTitle title={t('text:Associated_Bank_Account_Information')} />
        <TableBorder>
          <TableHeader header={bankAccountInfoTableHeaders} />
          <TableBody numOfCol={8}>
            {bankAccountList?.content.map((item, i) => {
              return (
                <Tr key={i}>
                  <Td data={item.anchorAgreementContractNo} />
                  <Td data={item.anchorName} />
                  <Td data={item.responsibleFinancierName} />
                  <Td data={item.responsibleBranchName} />
                  <Td data={item.partnerName} />
                  <Td data={item.partnerTaxCode} />
                  <Td data={item.accountOwner} />
                  <Td data={item.account} />
                </Tr>
              );
            })}
          </TableBody>
        </TableBorder>
        <Pagination pageable={bankAccountPageable.pageable} paginate={bankAccountPaginate} />
      </div>
    );
  };

  const renderEditHistory = () => {
    const editHistoryTableHeaders: HeaderType[] = [
      {
        headerText: t('text:Edited_Time'),
        colWidths: 150,
      },
      {
        headerText: t('text:Bank_Code'),
        colWidths: 100,
      },
      {
        headerText: t('text:Bank_Name'),
        colWidths: 130,
      },
      {
        headerText: t('text:Branch_Code'),
        colWidths: 130,
      },
      {
        headerText: t('text:Branch_Name'),
        colWidths: 130,
      },
      {
        headerText: t('text:Active_Status'),
        colWidths: 100,
      },
      {
        headerText: t('text:Edit_requested_by_User_ID/Name'),
        colWidths: 210,
      },
    ];

    const historyPaginate = async (pageNumber: number, sizePerPage: number) => {
      try {
        const fetchBankCodeHistory = await requestSyWaitingBankCodeList(pageNumber, sizePerPage, {
          targetBankCodeId: bankCodeId,
        });
        ReactDOM.unstable_batchedUpdates(() => {
          setWaitingBankCodeList(fetchBankCodeHistory);
          editHistoryPageable.setPageable(fetchBankCodeHistory);
        });
      } catch (e) {
        modal.show(e);
      }
    };

    return (
      <div className="content-area">
        <SectionTitle title={t('text:Information_Edit_History')} />
        <TableBorder>
          <TableHeader header={editHistoryTableHeaders} />
          <TableBody numOfCol={7}>
            {waitingBankCodeList?.content.map((item, i) => {
              return (
                <Tr key={i}>
                  <Td data={item.updatedDateTime} format="datetime" />
                  <Td data={item.bankCode} />
                  <Td data={item.bankName} />
                  <Td data={item.branchCode} />
                  <Td data={item.branchName} />
                  <Td data={item.deleted ? t('text:Deactivated') : t('text:Active')} />
                  <Td data={`${item.operatorUserLoginId} / ${item.operatorUserName}`} />
                </Tr>
              );
            })}
          </TableBody>
        </TableBorder>
        <Pagination pageable={editHistoryPageable.pageable} paginate={historyPaginate} />
      </div>
    );
  };

  return (
    <>
      <BackHeaderTitle title={t('text:Bank_Code_Details')} />
      <GuideMessage
        message={[
          t('text:On_this_page_you_can_check_the_bank_code_associated_bank_account_and_edit_history_information'),
          t('text:Click_on_the_Edit_button_to_request_revision_of_the_bank_code_information'),
        ]}
      />
      <div className="content-area">
        <SectionTitle title={t('text:Bank_Code_Information')}>{renderButtons()}</SectionTitle>
        <FormBorder editable={editable}>
          <FormSubtitle title={t('text:Bank_Code_Information')} backGroundType={BackGroundType.DarkGray} />
          <FormContents>
            <div className="row">
              <input type="text" name="financierId" hidden ref={register} />
              <FormInput
                label={t('text:Financier_Name')}
                value={bankCodeDetail?.financierName ?? ''}
                disabled
                error={errors.financierId}
              />
            </div>
            <div className="row">
              <FormInput
                label={t('text:Bank_Code')}
                disabled={!editable}
                requiredOptions={{ required: true }}
                name="bankCode"
                ref={register}
                error={errors.bankCode}
              />
              <FormInput
                label={t('text:Bank_Name')}
                disabled={!editable}
                requiredOptions={{ required: true }}
                name="bankName"
                ref={register}
                error={errors.bankName}
              />
            </div>
            <div className="row">
              <FormInput
                label={t('text:Branch_Code')}
                disabled={!editable}
                requiredOptions={{ required: true }}
                name="branchCode"
                ref={register}
                error={errors.branchCode}
              />
              <FormInput
                label={t('text:Branch_Name')}
                disabled={!editable}
                requiredOptions={{ required: true }}
                name="branchName"
                ref={register}
                error={errors.branchName}
              />
            </div>
            <div className="row">
              <FormRadioWrap label={t('text:Bank_code_Activation_Status')} error={errors.deleted}>
                <FormRadio
                  label={t('text:Activate')}
                  name="deleted"
                  value="false"
                  ref={register}
                  disabled={!editable}
                />
                <FormRadio
                  label={t('text:Deactivate')}
                  name="deleted"
                  value="true"
                  ref={register}
                  disabled={!editable}
                />
              </FormRadioWrap>
            </div>
          </FormContents>
        </FormBorder>
      </div>
      {bankAccountInfoTable()}
      {renderEditHistory()}
    </>
  );
}

export default SystemBankCodeDetailConfirmed;
